/**
 * "Discard changes?" for every form layer (TZ §9).
 *
 * `useDiscardGuard(open, dirty, onClose)` registers the layer and hands back
 * `requestClose`: Escape / ✕ / Cancel / overlay click go through it — a clean
 * form closes at once, a dirty one raises the confirm. The confirm is its own
 * layer (the next Escape only dismisses it) and is rendered ONCE by
 * `DiscardConfirmHost` from a tiny store here, so a modal does not have to
 * find a place in its JSX for it. Success paths (Apply / Save) call the raw
 * `onClose` — they are not questions.
 *
 * `useTouchedDirty(snapshot)` is the dirty check for big forms that seed
 * state asynchronously (default app, loaded rule): the baseline follows the
 * snapshot until the person first touches the form, so a value that arrived
 * from the server never counts as an edit.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLayer } from "@/lib/layer-stack";

export type DiscardPrompt = {
  id: number;
  title: string | null;
  text: string | null;
  keep: () => void;
  discard: () => void;
};

let prompts: DiscardPrompt[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
function top(): DiscardPrompt | null {
  return prompts.length ? prompts[prompts.length - 1] : null;
}

export function useTopDiscardPrompt(): DiscardPrompt | null {
  return useSyncExternalStore(subscribe, top, () => null);
}

export function useDiscardGuard(
  open: boolean,
  dirty: boolean,
  onClose: () => void,
  text?: string,
  title?: string,
): { requestClose: () => void; confirmOpen: boolean; keep: () => void; discard: () => void } {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!open) setConfirmOpen(false);
  }, [open]);

  const requestClose = useCallback(() => {
    if (dirtyRef.current) setConfirmOpen(true);
    else closeRef.current();
  }, []);
  const keep = useCallback(() => setConfirmOpen(false), []);
  const discard = useCallback(() => {
    setConfirmOpen(false);
    closeRef.current();
  }, []);

  useLayer(open, requestClose);
  const showing = open && confirmOpen;
  useLayer(showing, keep);
  useEffect(() => {
    if (!showing) return;
    const p: DiscardPrompt = { id: nextId++, title: title ?? null, text: text ?? null, keep, discard };
    prompts = [...prompts, p];
    emit();
    return () => {
      prompts = prompts.filter((x) => x !== p);
      emit();
    };
  }, [showing, text, title, keep, discard]);

  return { requestClose, confirmOpen: showing, keep, discard };
}

export type TouchProps = {
  onPointerDownCapture: () => void;
  onKeyDownCapture: () => void;
};

export function useTouchedDirty(snapshot: string): { dirty: boolean; touchProps: TouchProps } {
  const touched = useRef(false);
  const base = useRef(snapshot);
  // Until the first interaction the baseline tracks whatever the form
  // seeds itself with (server data, default app, prefilled time).
  useEffect(() => {
    if (!touched.current) base.current = snapshot;
  });
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const touch = useCallback(() => {
    if (!touched.current) {
      base.current = snapshotRef.current;
      touched.current = true;
    }
  }, []);
  return {
    dirty: touched.current && snapshot !== base.current,
    touchProps: { onPointerDownCapture: touch, onKeyDownCapture: touch },
  };
}
